import { useMemo } from 'react'

import { ColumnConditionValue, IActionRepeaterValue, TypographyGroupValue } from '@makyoapp/makyo-common'
import {
  cn,
  ColorVariant,
  HoverEffectVariant,
  HoverIntensity,
  parseSpacing,
  RawSpacingInput,
  Skeleton,
  SkeletonGroup,
  spacingToStyles,
  Timelines,
  TypographyValue,
  useMtwContext,
  useRtl,
} from '@makyoapp/makyo-tw-ui'

import { CoreComponentAction, CoreComponentInfos, CoreComponentTableData } from '@/core/models/app-components/types'
import { IRow } from '@/core/models/tables/row'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useCompTableData } from '@/hooks/__project__/useCompTableData'
import { ComponentSettingKey, SpacingInputValue } from '@/shared/builder'
import { formatMargin } from '@/shared/builder-utils'
import { getSafely } from '@/shared/safely'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { useCompPagination } from '../../hooks/useCompPagination'
import { CompPagination } from '../../shared/CompPagination'

export interface CompTimelineWrapperCoreProps {
  infos: CoreComponentInfos
  action: CoreComponentAction
  tableData: CoreComponentTableData
}

export function CompTimelineWrapperCore({
  infos: { settings, component, currentParentBg },
  tableData: { dbTable: table },
  action: { simulateAction },
}: CompTimelineWrapperCoreProps) {
  const { globalDoc } = useMtwContext()
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()
  const dir = useRtl()
  // ========================= data =========================
  const compId = component._id

  // columns
  const colTitle = settings[ComponentSettingKey.TimelineDataTitle] as string
  const colContent = settings[ComponentSettingKey.TimelineDataContent] as string
  const colDate = settings[ComponentSettingKey.TimelineDataDate] as string
  const colIcon = settings[ComponentSettingKey.TimelineDataIcon] as string
  const colIconColor = settings[ComponentSettingKey.TimelineDesignIconColorColumn] as string
  const isIconColorCustom = settings[ComponentSettingKey.TimelineDesignIconColorCustom] as boolean
  const colGroupBy = settings[ComponentSettingKey.TimelineDataGroupBy] as string

  // pagination
  const datasetLimit = settings[ComponentSettingKey.TimelineDataSetLimit]
  const isLimited = datasetLimit?.enabled
  const limit = datasetLimit?.value ?? 0
  const maxPerItems = isLimited ? limit : 10000

  const numItems = settings[ComponentSettingKey.TimelineDesignNumItems]

  const { pagination, setPagination } = useCompPagination({
    compId,
    defaultPerPage: Math.min(maxPerItems, numItems ?? 10),
  })

  const showPagination = settings[ComponentSettingKey.TimelineDesignPagination]
  const paginationAlign = settings[ComponentSettingKey.TimelineDesignPaginationAlignment] ?? 'center'

  const filterSetting = settings[ComponentSettingKey.TimelineDataFilter] as ColumnConditionValue | undefined
  const sortType = settings[ComponentSettingKey.TimelineDataSortType] as string
  const sortBy = settings[ComponentSettingKey.TimelineDataSortBy] as string

  // fetch all rows
  const [{ rows: allRows, filteredRowsCount, allRowsCount }, isLoading] = useCompTableData({
    table,
    componentId: component._id,
    filterSetting,
    sortSetting: { sortType, sortBy },
    pagination,
  })

  const totalPages = useMemo(() => {
    return Math.ceil(Math.min(filteredRowsCount, maxPerItems) / numItems)
  }, [filteredRowsCount, maxPerItems, numItems])

  const items = useMemo(() => {
    // Limit items based on numItems or default 10
    const maxItems = numItems || 10
    const limitedRows = allRows.slice(0, maxItems)

    const groupByCol = table.columnsMap.get(colGroupBy)
    if (colGroupBy && groupByCol) {
      const grouped = limitedRows.reduce((acc: Record<string, IRow[]>, currentRow) => {
        const groupKey = currentRow[groupByCol._id].toString()
        acc[groupKey] = acc[groupKey] ?? []
        acc[groupKey].push(currentRow)
        return acc
      }, {})

      const groupEntries = Object.entries(grouped)
      return groupEntries.map(([_groupKey, groupRows], groupIndex) => ({
        groupTitle: groupRows[0][colGroupBy]?.toString() ?? '',
        groupSparatorTitle: '',
        items: groupRows.map((row) => ({
          id: row._id,
          title: row[colTitle]?.toString() ?? '',
          content: row[colContent]?.toString() ?? '',
          time: row[colDate]?.toString() ?? '',
          icon: row[colIcon]?.toString() ?? '',
          iconColor: isIconColorCustom ? row[colIconColor]?.toString() : undefined,
        })),
        addSeparatorAfter: groupIndex < groupEntries.length - 1,
      }))
    } else {
      return limitedRows.map((row) => ({
        id: row._id,
        title: row[colTitle]?.toString() ?? '',
        content: row[colContent]?.toString() ?? '',
        time: row[colDate]?.toString() ?? '',
        icon: row[colIcon]?.toString() ?? '',
        iconColor: isIconColorCustom ? row[colIconColor]?.toString() : undefined,
      }))
    }
  }, [
    colGroupBy,
    table.columnsMap,
    allRows,
    colContent,
    colTitle,
    colDate,
    colIcon,
    isIconColorCustom,
    colIconColor,
    numItems,
  ])

  const shouldShowPagination = showPagination && totalPages > 1 && filteredRowsCount > numItems

  // ========================= styles =========================
  const typoSettings = settings[ComponentSettingKey?.TimelineDesignTypography] as TypographyGroupValue
  const titleTypography = typoSettings[ComponentSettingKey?.TimelineDesignTitleTypography] as TypographyValue
  const contentTypography = typoSettings[ComponentSettingKey?.TimelineDesignContentTypography] as TypographyValue
  const dateTypography = typoSettings[ComponentSettingKey?.TimelineDesignDateTypography] as TypographyValue
  const groupTitleTypography = typoSettings[ComponentSettingKey?.TimelineDesignGroupTitleTypography] as TypographyValue

  const iconColor = settings[ComponentSettingKey?.TimelineDesignIconColor] as ColorVariant
  const iconBackgroundColor = settings[ComponentSettingKey?.TimelineDesignIconBackgroundColorVariant] as ColorVariant
  const iconColorHex = globalDoc?.documentElement
    ? getComputedStyle(globalDoc.documentElement!).getPropertyValue(`--color-${iconColor}`)
    : undefined

  const timelineLineColor = settings[ComponentSettingKey?.TimelineDesignTimelineLineColor] as ColorVariant
  const orientation = settings[ComponentSettingKey?.TimelineDesignOrientation] as 'vertical' | 'horizontal'
  const margin = formatMargin(settings[ComponentSettingKey?.TimelineDesignMargin] as SpacingInputValue)
  const itemGap = settings[ComponentSettingKey?.TimelineDesignItemGap]
  const width = getSafely(settings, [ComponentSettingKey.TimelineDesignWidth], ['100%'])[0]
  const titleLines = settings[ComponentSettingKey?.TimelineDesignTitleLines]
  const contentLines = settings[ComponentSettingKey?.TimelineDesignContentLines]
  const align = settings[ComponentSettingKey?.TimelineDesignCompHAlign]

  const hoverEffect = settings[ComponentSettingKey?.TimelineDesignHoverEffect] as HoverEffectVariant
  const hoverIntensity = 'md' as HoverIntensity

  // text color
  const titleColor = settings[ComponentSettingKey?.TimelineDesignTitleColor]
  const titleColorSolid = settings[ComponentSettingKey?.TimelineDesignTitleColorSolid] || 'primary-40'
  const titleColorStartGradient = settings[ComponentSettingKey?.TimelineDesignTitleColorStartGradient] || 'primary-40'
  const titleColorEndGradient = settings[ComponentSettingKey?.TimelineDesignTitleColorEndGradient] || 'primary-40'
  const titleColorGradientDirection = settings[ComponentSettingKey?.TimelineDesignTitleColorGradientDirection]

  const contentColor = settings[ComponentSettingKey?.TimelineDesignContentColor]
  const contentColorSolid = settings[ComponentSettingKey?.TimelineDesignContentColorSolid] || 'primary-40'
  const contentColorStartGradient =
    settings[ComponentSettingKey?.TimelineDesignContentColorStartGradient] || 'primary-40'
  const contentColorEndGradient = settings[ComponentSettingKey?.TimelineDesignContentColorEndGradient] || 'primary-40'
  const contentColorGradientDirection = settings[ComponentSettingKey?.TimelineDesignContentColorGradientDirection]

  const dateColor = settings[ComponentSettingKey?.TimelineDesignDateColor]
  const dateColorSolid = settings[ComponentSettingKey?.TimelineDesignDateColorSolid] || 'primary-40'
  const dateColorStartGradient = settings[ComponentSettingKey?.TimelineDesignDateColorStartGradient] || 'primary-40'
  const dateColorEndGradient = settings[ComponentSettingKey?.TimelineDesignDateColorEndGradient] || 'primary-40'
  const dateColorGradientDirection = settings[ComponentSettingKey?.TimelineDesignDateColorGradientDirection]

  const groupTitleColor = settings[ComponentSettingKey?.TimelineDesignGroupTitleColor]
  const groupTitleColorSolid = settings[ComponentSettingKey?.TimelineDesignGroupTitleColorSolid] || 'primary-40'
  const groupTitleColorStartGradient =
    settings[ComponentSettingKey?.TimelineDesignGroupTitleColorStartGradient] || 'primary-40'
  const groupTitleColorEndGradient =
    settings[ComponentSettingKey?.TimelineDesignGroupTitleColorEndGradient] || 'primary-40'
  const groupTitleColorGradientDirection = settings[ComponentSettingKey?.TimelineDesignGroupTitleColorGradientDirection]

  // ========================= actions =========================
  const actions = getSafely(settings, [ComponentSettingKey.TimelineActionItemClick], []) as IActionRepeaterValue[]

  const handleClick = (rowId: string) => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
    if (!!actions.length) {
      simulateAction({ actions, rowId, tableId: table.settings._id })
    }
  }

  const handleComponentClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  const timelineComponent = (
    <div
      className='w-full p-8'
      style={{
        ...(dir === 'rtl' &&
          ({
            '--timeline-icon-left': 'auto',
            '--timeline-icon-right': '-1rem',
          } as React.CSSProperties)),
      }}
    >
      <Timelines
        items={items as any}
        orientation={orientation}
        dateTypography={dateTypography}
        groupTitleTypography={groupTitleTypography}
        titleTypography={titleTypography}
        subtitleTypography={contentTypography}
        iconColor={iconColorHex}
        lineColor={timelineLineColor}
        isIconDynamicColor={isIconColorCustom}
        iconBackgroundColor={iconBackgroundColor}
        backgroundColor={currentParentBg ?? 'neutral-100'}
        gap={itemGap.value}
        margin={margin as any}
        width={width}
        textLineTitle={titleLines ?? 1}
        textLineContent={contentLines ?? 1}
        align={align}
        onItemClick={handleClick}
        textColorType={titleColor}
        textColorSolid={titleColorSolid}
        textColorStartGradient={titleColorStartGradient}
        textColorEndGradient={titleColorEndGradient}
        textColorGradientDirection={titleColorGradientDirection}
        contentColorType={contentColor}
        contentColorSolid={contentColorSolid}
        contentColorStartGradient={contentColorStartGradient}
        contentColorEndGradient={contentColorEndGradient}
        contentColorGradientDirection={contentColorGradientDirection}
        dateColorType={dateColor}
        dateColorSolid={dateColorSolid}
        dateColorStartGradient={dateColorStartGradient}
        dateColorEndGradient={dateColorEndGradient}
        dateColorGradientDirection={dateColorGradientDirection}
        groupTitleColorType={groupTitleColor}
        groupTitleColorSolid={groupTitleColorSolid}
        groupTitleColorStartGradient={groupTitleColorStartGradient}
        groupTitleColorEndGradient={groupTitleColorEndGradient}
        groupTitleColorGradientDirection={groupTitleColorGradientDirection}
        hoverEffect={hoverEffect as HoverEffectVariant}
        hoverIntensity={hoverIntensity as HoverIntensity}
      />
      {shouldShowPagination && (
        <div
          className={cn('flex justify-center mt-8')}
          style={{
            justifyContent: paginationAlign,
          }}
        >
          <CompPagination
            totalPages={totalPages}
            perPage={pagination.perPage}
            page={pagination.page}
            onChange={setPagination}
            backgroundColor={currentParentBg}
          />
        </div>
      )}
    </div>
  )

  if (isLoading) {
    return <TimelineSkeleton backgroundColor={currentParentBg!} margin={margin} />
  }

  if (isDevelopmentMode) {
    return (
      <div className='relative inline-block w-full border border-dashed' style={{ borderColor: '#333' }}>
        {timelineComponent}
        <CompOverlayDevmode handleClick={handleComponentClick} />
      </div>
    )
  }

  return timelineComponent
}

const TimelineSkeleton = ({ backgroundColor, margin }: { backgroundColor: ColorVariant; margin: RawSpacingInput }) => {
  return (
    <SkeletonGroup backgroundColor={backgroundColor}>
      <div className='w-full' style={{ ...spacingToStyles(parseSpacing(margin), 'padding') }}>
        <div className='p-8 space-y-6'>
          {[...new Array(4)].map((_, i) => (
            <div key={i} className='flex gap-4'>
              <Skeleton className='-ml-4 w-8 h-8 rounded-full shrink-0' />
              <div className='flex-1 space-y-2'>
                <Skeleton className='w-[20%] h-5 rounded-lg' />
                <Skeleton className='w-[50%] h-6 rounded-lg' />
                <Skeleton className='w-[10%] h-5 rounded-lg' />
              </div>
            </div>
          ))}
        </div>
      </div>
    </SkeletonGroup>
  )
}
